import { TopBar } from '@/layouts/TopBar';
import Paths from '@/routes/paths';
import JInfoBox from '@atoms/JInfoBox';
import Jpaper from '@atoms/JPaper';
import { JVideoPlayer } from '@atoms/JVideoPlayer';
import { Loading } from '@atoms/Loading';
import { StatusChip } from '@atoms/StatusChip';
import JobStatusEnum from '@jobs/models/job-status-enum';
import { useGetJobById } from '@jobs/queries/useGetJobs';
import { getJobStatus } from '@jobs/utils';
import { formatDate } from '@lib/dateUtils';
import Address from '@models/address';
import { Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

const JobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  if (!id) return <></>;
  const { data, isLoading } = useGetJobById(id);
  const job = data?.data;
  if (!job) return <></>;
  const jobStatus = getJobStatus(
    !!job?.deletedAt,
    job?.publicationStartDate,
    job?.publicationEndDate,
  );

  const formatAddress = (inputData: Address | null): string | null => {
    if (!inputData) return null;
    const street = inputData?.street;
    const city = inputData.city?.name;
    const zipCode = inputData.city?.zipCode;
    const country = inputData.country.name;

    const transformedData = `${street}, ${city} ${zipCode}, ${country}.`;
    return transformedData;
  };
  if (isLoading) return <Loading />;
  return (
    <>
      <TopBar
        navigateToBack={() => navigate(Paths.JobsPage)}
        title={
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <Typography variant="h2">{t('JOB')}</Typography>{' '}
            <StatusChip
              label={t(jobStatus.toUpperCase())}
              variant={
                jobStatus === JobStatusEnum.PUBLISHED ||
                jobStatus === JobStatusEnum.PENDING_PUBLICATION
                  ? 'success'
                  : 'danger'
              }
            />
          </Stack>
        }
      />
      <Stack direction={'row'} gap={2} mt={3} p={2}>
        <Jpaper flex={1}>
          <Typography variant="h3" mb={3}>
            {t('DETAILS')}
          </Typography>
          <JInfoBox label={t('REFERENCE')} value={job.referenceNumber} bold />
          <JInfoBox label={t('OFFER')} value={job.title} bold />
          <JInfoBox
            label={t('NAME_ENTERPRISE')}
            value={job.establishment.enterprise.name}
          />
          <JInfoBox
            label={t('NAME_ESTABLISHMENT')}
            value={job.establishment.name}
          />
          <JInfoBox
            label={t('SECTOR')}
            value={job.activityArea.romeDomain.name}
          />

          {job.address && (
            <JInfoBox
              label={t('ADRESS')}
              value={formatAddress(job.address) ?? ''}
            />
          )}

          <JInfoBox label={t('DESCRIPTION')} value={job.description} />
        </Jpaper>
        <Jpaper flex={1}>
          <Typography variant="h3" mb={3}>
            {t('CONTRACT')}
          </Typography>
          <JInfoBox
            label={t('QUALIFICATIONS')}
            value={job.qualifications
              .map((item) => item.romeForm.name)
              .join(',')}
          />
          <JInfoBox
            label={t('CONTRACT_TYPE')}
            value={job.contractType.toUpperCase()}
          />
          <JInfoBox
            label={t('DURATION')}
            value={`${formatDate(job.jobStartDate)} - ${formatDate(
              job.jobEndDate,
            )}`}
          />
          <JInfoBox
            label={t('HOUR_TYPE')}
            value={t(job.hourlyType.toUpperCase())}
          />
          <JInfoBox
            label={t('SCHEDULES')}
            value={`${job.dailyStartHour?.substring(
              0,
              5,
            )} - ${job.dailyEndHour?.substring(0, 5)}`}
          />
          <JInfoBox label={t('HOURLY_RATE')} value={`${job.hourlyWage}€`} />
        </Jpaper>
        <Jpaper flex={1}>
          <Typography variant="h3" mb={3}>
            {t('VIDEO_TITLE')}
          </Typography>
          <JVideoPlayer
            autoHeight
            videoUrl={job.videoUrl}
            thumbnailUrl={job.thumbnailUrl}
          />
        </Jpaper>
      </Stack>
    </>
  );
};

export default JobDetails;
